import { Link } from "react-router-dom";
import SiteShell from "@/components/feature/SiteShell";
import { siteConfig } from "@/config/site";
import FeedbackForm from "./components/FeedbackForm";

const services = [
  {
    icon: "ri-newspaper-line",
    title: "影视资讯速递",
    description: "整理上映动态、热度榜单、幕后故事与片单推荐，用短资讯的方式让你在几分钟内了解最近发生了什么。",
  },
  {
    icon: "ri-stack-line",
    title: "分类与筛选",
    description: "电影与电视剧两大板块各自细分类型，支持按地区、年份组合筛选，热门、最新、评分三种排序随手切换。",
  },
  {
    icon: "ri-compass-discover-line",
    title: "作品详情与推荐",
    description: "每部作品都配有简介、主创与关联标签，详情页还会根据类型与导演给出相似推荐，避免挑片时无从下手。",
  },
];

const promises = [
  "不设注册门槛，打开即可浏览全部内容",
  "不提供在线播放与下载，仅做资料与推荐",
  "所有演职人员与评分均为演示内容",
  "移动端与桌面端保持一致的阅读体验",
];

export default function AboutPage() {
  return (
    <SiteShell
      breadcrumb="/about"
      pageTitle="关于我们 - 长日光阴影视门户"
      pageDescription="了解长日光阴的网站定位、内容范围与服务说明。长日光阴致力于提供最新电影、电视剧资讯和影视资源推荐，帮助影视爱好者高效找到想看的内容。"
      pageKeywords="长日光阴,关于我们,影视门户,影视资讯,影视推荐"
    >
      <div className="pt-16 md:pt-[72px]">
        <section className="relative w-full overflow-hidden bg-primary-950">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=editorial%20desk%20with%20open%20film%20magazine%20vintage%20camera%20and%20warm%20desk%20lamp%20against%20deep%20navy%20wall%20cinematic%20moody%20still%20life%20no%20text&width=1920&height=700&seq=about-hero&orientation=landscape"
              alt="长日光阴编辑部场景"
              className="h-full w-full object-cover object-top opacity-55"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/88 to-primary-900/50"></div>
          </div>
          <div className="relative z-10 mx-auto max-w-[1440px] px-4 py-14 md:px-8 md:py-20">
            <p className="text-[10px] tracking-[0.28em] text-primary-200">ABOUT {siteConfig.englishName}</p>
            <h1 className="mt-3 max-w-3xl font-heading text-3xl font-bold leading-snug text-background-50 md:text-4xl">
              关于长日光阴
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
              我们相信，看电影和追剧本来就该是一件轻松的事。长日光阴把散落各处的影视信息重新整理成清晰的分类、可比较的列表和足够完整的作品页，让你少走弯路，多看一点好内容。
            </p>
            <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                { value: "24", label: "精选作品" },
                { value: "2", label: "内容主分类" },
                { value: "6", label: "资讯栏目" },
                { value: "0", label: "弹窗广告" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg bg-background-50/10 px-4 py-3 backdrop-blur">
                  <p className="font-heading text-2xl font-bold text-secondary-400">{stat.value}</p>
                  <p className="mt-1 text-[11px] text-primary-200">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="mx-auto max-w-[1440px]">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <p className="mb-2 flex items-center gap-2 text-xs tracking-[0.18em] text-accent-600">
                  <span className="inline-block h-3 w-0.5 bg-accent-500"></span>
                  网站定位
                </p>
                <h2 className="font-heading text-2xl font-bold text-foreground-950">
                  一个专注于“找片”的影视门户
                </h2>
                <p className="mt-4 text-sm leading-7 text-foreground-700">
                  长日光阴面向所有关注影视动态、习惯追剧的普通观众。我们不做影评聚合的搬运工，也不做时效性极强的话题追热点，而是把注意力放在内容结构上：清楚分类、可靠筛选、完整详情。
                </p>
                <p className="mt-3 text-sm leading-7 text-foreground-700">
                  无论你是想找一部周末下饭剧，还是想系统地补完某个类型片单，都可以从分类页开始，通过筛选与排序一步步收窄范围，直到找到那一部。
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/category/movie"
                    className="inline-flex items-center gap-2 rounded-md bg-primary-800 px-4 py-2.5 text-sm text-background-50 whitespace-nowrap transition-colors cursor-pointer hover:bg-primary-700"
                  >
                    <i className="ri-film-line" aria-hidden="true"></i>
                    电影分类
                  </Link>
                  <Link
                    to="/category/tv"
                    className="inline-flex items-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-800 whitespace-nowrap transition-colors cursor-pointer hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className="ri-live-line" aria-hidden="true"></i>
                    电视剧分类
                  </Link>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {services.map((service) => (
                    <div key={service.title} className="rounded-lg border border-background-200 bg-background-100 p-4">
                      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                        <i className={`${service.icon} text-base`} aria-hidden="true"></i>
                      </span>
                      <h3 className="mt-3 text-sm font-medium text-foreground-900">{service.title}</h3>
                      <p className="mt-2 text-xs leading-relaxed text-foreground-600">{service.description}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-4 rounded-lg border border-secondary-200 bg-secondary-50 p-4 md:p-5">
                  <h3 className="flex items-center gap-2 text-sm font-medium text-foreground-900">
                    <i className="ri-shield-check-line text-secondary-700" aria-hidden="true"></i>
                    用户权益提示
                  </h3>
                  <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {promises.map((promise) => (
                      <li key={promise} className="flex items-start gap-2 text-xs leading-relaxed text-secondary-900">
                        <i className="ri-check-line mt-0.5 text-secondary-700" aria-hidden="true"></i>
                        {promise}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-y border-background-200 bg-background-100 px-4 py-12 md:px-8 md:py-16">
          <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="mb-2 flex items-center gap-2 text-xs tracking-[0.18em] text-accent-600">
                <span className="inline-block h-3 w-0.5 bg-accent-500"></span>
                联系我们
              </p>
              <h2 className="font-heading text-2xl font-bold text-foreground-950">说点什么吧</h2>
              <p className="mt-3 text-sm leading-7 text-foreground-700">
                发现内容有误、想推荐一部被你私藏的片子，或者希望与我们合作，都可以通过下面的表单或邮箱联系我们。我们通常在两个工作日内回复。
              </p>

              <dl className="mt-6 space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-700">
                    <i className="ri-mail-line" aria-hidden="true"></i>
                  </span>
                  <div>
                    <dt className="text-xs text-foreground-500">邮箱</dt>
                    <dd>
                      <a
                        href={`mailto:${siteConfig.contactEmail}`}
                        className="text-foreground-900 transition-colors cursor-pointer hover:text-primary-700"
                      >
                        {siteConfig.contactEmail}
                      </a>
                    </dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-700">
                    <i className="ri-map-pin-line" aria-hidden="true"></i>
                  </span>
                  <div>
                    <dt className="text-xs text-foreground-500">编辑部地址</dt>
                    <dd className="text-foreground-900">{siteConfig.address}</dd>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-700">
                    <i className="ri-time-line" aria-hidden="true"></i>
                  </span>
                  <div>
                    <dt className="text-xs text-foreground-500">回复时间</dt>
                    <dd className="text-foreground-900">工作日 10:00 - 18:00</dd>
                  </div>
                </div>
              </dl>
            </div>

            <div className="lg:col-span-7">
              <FeedbackForm />
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-12 md:py-16">
          <div className="mx-auto max-w-[1440px]">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">关于演示内容</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
              {[
                {
                  title: "影视资料",
                  text: "站内所有片名、导演、演员、评分与简介均为虚构演示内容，用于展示页面结构与交互效果。",
                },
                {
                  title: "搜索与筛选",
                  text: "搜索、筛选与排序均在前端完成，结果来自站内演示数据，不代表真实片库情况。",
                },
                {
                  title: "分享与反馈",
                  text: "分享按钮生成的是站内链接，反馈表单会真实提交，我们会读取你的留言与联系方式。",
                },
              ].map((block) => (
                <div key={block.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h3 className="text-sm font-medium text-foreground-900">{block.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{block.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </SiteShell>
  );
}